import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, FormControlLabel, Link, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { GrantStatus, VisitationAgendaCaseModel, VisitationAgendaModel, VisitationBookingStatus, VisitationDecision, VisitationProposal } from '../../api-service/clients';
import { sanitizeHtml } from '../../utilities/sanitize-html';
import { formatDate } from '../../utilities';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { isContinuationCase, VisitationAssessmentDraft } from './visitation-assessment-draft';
import type { VisitationTeamPageComponentProps } from './visitation-team-page-component';

type VisitationAgendaComponentProps = VisitationTeamPageComponentProps & { agenda: VisitationAgendaModel };

const statusLabels: Record<VisitationBookingStatus, string> = {
  [VisitationBookingStatus.Reserved]: 'Reserveret',
  [VisitationBookingStatus.Processed]: 'Behandlet',
  [VisitationBookingStatus.Postponed]: 'Udsat',
  [VisitationBookingStatus.Cancelled]: 'Annulleret',
};

function time(value: Date | string): string {
  return format(new Date(value), 'HH:mm');
}

function rulingUrl(agendaCase: VisitationAgendaCaseModel): string {
  const resource = agendaCase.rulingContinuationId ? 'citizen-ruling-continuation' : 'citizen-ruling';
  return `/gotoresource/${resource}?resourceId=${agendaCase.rulingId}&rootEntityId=${agendaCase.citizenId}`;
}

export function VisitationAgendaComponent(props: VisitationAgendaComponentProps) {
  const { agenda, drafts, isSaving, onCloseAgenda, onReopenAgenda, onCompressAgenda, onCancelMeeting } = props;
  const isClosed = !!agenda.agendaClosedAt;
  const canCancel = !agenda.isCancelled && new Date(agenda.startAt) > new Date();
  const openCases = agenda.cases.filter(agendaCase => agendaCase.status === VisitationBookingStatus.Reserved).length;
  const [confirmCancel, setConfirmCancel] = useState(false);

  function handleConfirmCancel() {
    setConfirmCancel(false);
    onCancelMeeting();
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">
            {agenda.teamName} · {format(new Date(agenda.startAt), 'EEEE d. MMMM', { locale: da })} kl. {time(agenda.startAt)}–{time(agenda.endAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {agenda.location ? `${agenda.location} · ` : ''}
            {agenda.cases.length} {agenda.cases.length === 1 ? 'sag' : 'sager'} · {agenda.freeMinutes} min ledigt
            {isClosed ? ` · dagsorden lukket ${formatDate(agenda.agendaClosedAt as string)}` : ''}
          </Typography>
        </Box>
        {canCancel && (
          <Button variant="outlined" color="error" onClick={() => setConfirmCancel(true)} disabled={isSaving}>
            Aflys møde
          </Button>
        )}
        {!agenda.isCancelled && !isClosed && (
          <Button variant="outlined" onClick={onCompressAgenda} disabled={isSaving}>
            Komprimér
          </Button>
        )}
        {!agenda.isCancelled && (
          <Button variant="contained" onClick={isClosed ? onReopenAgenda : onCloseAgenda} disabled={isSaving}>
            {isClosed ? 'Genåbn dagsorden' : 'Luk dagsorden'}
          </Button>
        )}
      </Box>

      {agenda.isCancelled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Mødet er aflyst.
        </Alert>
      )}

      <ConfirmDialog
        type="warning"
        title="Aflys mødet?"
        message={
          openCases === 0
            ? 'Mødet aflyses og tilbydes ikke længere ved reservation.'
            : `${openCases} ${
                openCases === 1 ? 'reserveret sag annulleres' : 'reserverede sager annulleres'
              }, og sagsbehandlerne får en opgave om at reservere en ny tid. Mødet tilbydes ikke længere ved reservation.`
        }
        open={confirmCancel}
        onConfirmClick={handleConfirmCancel}
        onCancelClick={() => setConfirmCancel(false)}
        onClose={() => setConfirmCancel(false)}
        confirmButtonText="Aflys møde"
        cancelButtonText="Fortryd"
      />

      {agenda.cases.length === 0 && !agenda.isCancelled && <Alert severity="info">Der er ingen sager på mødet.</Alert>}

      {agenda.cases.map(agendaCase => (
        <VisitationAgendaCase key={agendaCase.visitationBookingId} {...props} agendaCase={agendaCase} draft={drafts[agendaCase.visitationBookingId]} />
      ))}
    </Box>
  );
}

type VisitationAgendaCaseProps = VisitationAgendaComponentProps & { agendaCase: VisitationAgendaCaseModel; draft: VisitationAssessmentDraft | undefined };

function VisitationAgendaCase(props: VisitationAgendaCaseProps) {
  const { agendaCase, draft, isSaving, onGrantDecisionChange, onGrantCommentChange, onReasoningChange, onProcess, onPostpone } = props;
  const isOpen = agendaCase.status === VisitationBookingStatus.Reserved;
  const isContinuation = isContinuationCase(agendaCase);
  const caseText = isContinuation ? agendaCase.continuationProposal : agendaCase.recommendation;

  return (
    <Accordion defaultExpanded={isOpen} disableGutters sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ minWidth: 96 }}>
            {time(agendaCase.startAt)}–{time(agendaCase.endAt)}
          </Typography>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {agendaCase.citizenName ?? 'Borger'}
            {isContinuation ? ' · fortsættelse' : ''}
          </Typography>
          {agendaCase.isOverbooked && <Chip size="small" color="warning" label="Overbooket" />}
          <Chip size="small" label={statusLabels[agendaCase.status]} color={isOpen ? 'default' : 'success'} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary">
          Sagsbehandler: {agendaCase.caseworkerName ?? 'Ikke angivet'} · Reserveret {agendaCase.reservedMinutes} min
          {agendaCase.determiningGrantTypeName ? ` (${agendaCase.determiningGrantTypeName})` : ''}
          {agendaCase.processingDeadline ? ` · Sagsbehandlingsfrist ${formatDate(agendaCase.processingDeadline)}` : ''}
        </Typography>
        <Link href={rulingUrl(agendaCase)} target="_blank" rel="noopener" variant="body2">
          Åbn {isContinuation ? 'fortsættelsen' : 'afgørelsen'}
        </Link>

        {caseText && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2">{isContinuation ? 'Forslag til fortsættelse' : 'Indstilling'}</Typography>
            <Box sx={{ typography: 'body2' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(caseText) }} />
          </Box>
        )}

        {!isOpen && agendaCase.teamReasoning && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">{agendaCase.status === VisitationBookingStatus.Postponed ? 'Årsag til udsættelse' : 'Teamets begrundelse'}</Typography>
            <Box sx={{ typography: 'body2' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(agendaCase.teamReasoning) }} />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Bevillinger</Typography>
          {agendaCase.grants.map(grant => (
            <Box key={grant.grantId} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', py: 0.5 }}>
              <Typography variant="body2" sx={{ minWidth: 200, flex: 1 }}>
                {grant.grantTypeName}
                {grant.visitationProposal
                  ? ` · forslag: ${
                      grant.visitationProposal === VisitationProposal.Granted ? 'tilkendes' : grant.visitationProposal === VisitationProposal.Rejected ? 'afvises' : 'ikke vurderet'
                    }`
                  : ''}
              </Typography>
              {isOpen && draft ? (
                <>
                  <RadioGroup
                    row
                    value={draft.grants[grant.grantId]?.decision ?? ''}
                    onChange={(_, value) => onGrantDecisionChange(agendaCase.visitationBookingId, grant.grantId, value as VisitationDecision)}
                  >
                    <FormControlLabel value={VisitationDecision.Granted} control={<Radio size="small" />} label="Tilkendt" />
                    <FormControlLabel value={VisitationDecision.Rejected} control={<Radio size="small" />} label="Afvist" />
                  </RadioGroup>
                  <TextField
                    size="small"
                    label="Visitationsbemærkning"
                    value={draft.grants[grant.grantId]?.comment ?? ''}
                    onChange={event => onGrantCommentChange(agendaCase.visitationBookingId, grant.grantId, event.target.value)}
                    sx={{ minWidth: 240, flex: 1 }}
                    inputProps={{ maxLength: 5000 }}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {grant.status === GrantStatus.Granted ? 'Tilkendt' : grant.status === GrantStatus.Rejected ? 'Afvist' : 'Ikke vurderet'}
                  {grant.visitationComment ? ` – ${grant.visitationComment}` : ''}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {isOpen && draft && (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Teamets samlede begrundelse"
              helperText="Journaliseres på borgeren. Sagsbehandleren skriver fortsat afgørelsen med en individuel begrundelse."
              value={draft.reasoning}
              onChange={event => onReasoningChange(agendaCase.visitationBookingId, event.target.value)}
              multiline
              minRows={3}
              fullWidth
              variant="filled"
            />
            {draft.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {draft.error}
              </Alert>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => onPostpone(agendaCase)} disabled={isSaving}>
                Udsæt sag
              </Button>
              <Button variant="contained" onClick={() => onProcess(agendaCase)} disabled={isSaving}>
                Registrér vurdering
              </Button>
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
